import type {PropsOf} from '@chakra-ui/react';
import {useInterval, useMergeRefs, useTheme} from '@chakra-ui/react';
import {ChakraDiv} from '@linx-security/ui/utils/chakra-elements';
import type {Ref} from 'react';
import {Children, cloneElement, forwardRef, useRef, useState} from 'react';
import {Bar, BarChart as RechartsBarChart, ResponsiveContainer, Tooltip} from 'recharts';

import {useSlots} from '../../hooks/useSlots';
import {ChartTooltip} from '../ChartTooltip';
import type {ChartItem} from '../PieChart';
import {skeletonCSS, skeletonVars} from '../Skeleton/theme';

import {parts} from './anatomy';
import {useStyles} from './context';
import {vars} from './vars';

type Props = {
  data?: Array<ChartItem & {total: number}>;
  isLoaded?: boolean;
  noOfSkeletonItems?: number;
} & Omit<PropsOf<typeof RechartsBarChart>, 'data'>;

type SlotsConfig = {
  bar: typeof Bar;
  tooltip: typeof ChartTooltip;
};

export const BarChart = forwardRef((props: Props, ref: Ref<HTMLDivElement>) => {
  const {children, data, isLoaded, noOfSkeletonItems, ...rest} = props;
  const {container} = useStyles();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mergedRef = useMergeRefs(containerRef, ref);
  // @ts-expect-error: Recharts types are not compatible with our slots system
  const [slots, childrenWithoutSlots] = useSlots<SlotsConfig, {}>(
    children,
    {
      tooltip: ChartTooltip,
      bar: Bar,
    },
    parts,
    {},
  );

  if (!isLoaded) {
    return <BarChartSkeleton noOfSkeletonItems={noOfSkeletonItems} />;
  }

  return (
    <ChakraDiv __css={container}>
      <ResponsiveContainer ref={mergedRef}>
        <RechartsBarChart barSize={12} data={data} {...rest}>
          {/* // We render the tooltip only if the tooltip slot is provided */}
          {slots.tooltip && (
            <Tooltip
              cursor={{fill: `transparent`}}
              content={props => {
                const children = Children.only(slots.tooltip?.props.children);
                return slots.tooltip ? cloneElement(children, {...props, containerRef}) : null;
              }}
            />
          )}

          {slots.bar && (
            <>
              {/* // The first bar is the total bar, which is rendered behind the filled bars */}
              <Bar
                name={`total`}
                tooltipType={`none`}
                radius={10000}
                dataKey={`total`}
                style={{transform: `translate(${16}px, 0)`, fill: vars.fill.reference}}
              />
              {/* // The filled bars */}

              {cloneElement(slots.bar, {
                radius: 10000,
                minPointSize: 5,
              })}
            </>
          )}
          {childrenWithoutSlots}
        </RechartsBarChart>
      </ResponsiveContainer>
    </ChakraDiv>
  );
});

const getItems = (size: number) => new Array(size).fill({total: 10_000, count: 5000, color: vars.fill.reference});
export const BarChartSkeleton = ({noOfSkeletonItems = 4}: {noOfSkeletonItems?: number}) => {
  const theme = useTheme();
  const [data, setData] = useState(getItems(noOfSkeletonItems));

  useInterval(() => {
    // If the count is 5000, we set it to 0 to simulate the animation
    const newData = data.map(item => ({...item, count: item.count === 5000 ? 0 : Math.floor(Math.random() * 10000)}));
    setData(newData);
  }, 400);

  return (
    <ChakraDiv __css={skeletonCSS(theme)} width={`100%`} height={`100%`}>
      <ResponsiveContainer>
        <RechartsBarChart barSize={12} data={data}>
          <Bar
            name={`total`}
            tooltipType={`none`}
            radius={10000}
            dataKey={`total`}
            style={{transform: `translate(${16}px, 0)`, fill: skeletonVars[`start-color`].reference}}
          />
          <Bar
            name={`total`}
            tooltipType={`none`}
            radius={10000}
            dataKey={`count`}
            style={{fill: skeletonVars[`end-color`].reference, opacity: 0.7}}
          />
        </RechartsBarChart>
      </ResponsiveContainer>
    </ChakraDiv>
  );
};
